import React from 'react';
import { Facebook, Instagram, Youtube } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/utils/languageUtils';
import WhatsAppIcon from '@/components/homepage/icons/WhatsAppIcon';

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Instagram, label: 'Instagram', href: t('social.instagram') },
    { 
      icon: () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64c.16 0 .29 0 .45.05V9.32a6.37 6.37 0 00-3.45 11.7 6.36 6.36 0 009.27-5.62l-.06-9.53a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-1.23l.13-.07z" />
        </svg>
      ), 
      label: 'TikTok', 
      href: t('social.tiktok')
    },
    { icon: Youtube, label: 'Youtube', href: t('social.youtube') },
    { icon: Facebook, label: 'Facebook', href: t('social.facebook') },
    { 
      icon: () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.665 3.717l-17.73 6.837c-1.21.486-1.203 1.161-.222 1.462l4.552 1.42 10.532-6.645c.498-.303.953-.14.579.192l-8.533 7.701h-.002l.002.001-.314 4.692c.46 0 .663-.211.921-.46l2.211-2.15 4.599 3.397c.848.467 1.457.227 1.668-.785l3.019-14.228c.309-1.239-.473-1.8-1.282-1.434z" />
        </svg>
      ), 
      label: 'Telegram', 
      href: t('social.telegram')
    },
  ];

  const footerLinks = [
    { name: t('footer.terms'), path: getLocalizedPath('/terms', language) },
    { name: t('footer.privacy'), path: getLocalizedPath('/privacy', language) },
    { name: t('footer.cookies'), path: getLocalizedPath('/cookies', language) },
  ];

  return (
    <footer className="bg-gray-900 border-t border-gray-800 pt-12 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <Link to="/" className="flex items-center mb-4">
              <img 
                src="/logo_1_small_inverted_no_background.png" 
                alt="The Club Logo" 
                className="h-10 w-auto" 
              />
            </Link>
            <p className="text-gray-400 mb-6 text-sm">
              &copy; {currentYear} The Club. {t('footer.rights')}
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((link, index) => {
                const Icon = link.icon;
                return (
                  <a 
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.label}
                    className="text-gray-400 hover:text-purple-400 transition-colors"
                  >
                    <Icon />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">
              {t('footer.theclub')}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to={getLocalizedPath('/', language)}
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                >
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link 
                  to={getLocalizedPath('/referral-program', language)}
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                >
                  {t('nav.referralOpportunity')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/radex" 
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                >
                  {t('footer.radex')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact-me" 
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                >
                  {t('footer.contactMe')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">
              {t('footer.conditions')}
            </h3>
            <ul className="space-y-2">
              {footerLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-purple-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Support */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">
              {t('footer.customersupport')}
            </h3>
            <ul className="space-y-2 text-gray-400">
              <li className="flex items-center">
                <Link 
                  to="/contact-us" 
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                >
                  {t('footer.contactUs')}
                </Link>
              </li>
              <li className="flex items-center">
                <Link 
                  to="/contact-us" 
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                >
                  {t('footer.contactuswhatsapp')}
                </Link>
              </li>
              <li className="flex items-center">
                <Link 
                  to="/contact-me" 
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                >
                  {t('footer.contactMe')}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
